/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Selectors as ChannelFiltersSelectors,
  ActionCreators as ChannelFiltersCreators,
  DEFAULT_FILTER_ID,
  ChannelFilter,
} from 'store/ducks/watchTv/channelFilters';
import { useDispatch, useSelector } from 'react-redux';
import { WatchTvBarProps, WatchTvSection } from 'types/components/WatchTv';
import { StoreState } from 'store/store';
import { DisabledFeaturesValues, Ducks, TimeUtils } from '@wit/vtv-x-foundation-lib';
import { getDayName } from 'services/utils/DateTimeUtils';
import translate from 'services/i18n/translate';
import Dropdown from '../dropdown';
import { Close } from '..';

function WatchTvBar({
  setFilter,
  section,
  setSection,
  overlay,
  setShowWrapper,
  selectedDay,
  setSelectedDay,
}: WatchTvBarProps): JSX.Element {
  const filters = useSelector((state: StoreState) => ChannelFiltersSelectors.channelFilters(state));
  const isFiltersDisabled = useSelector((store: StoreState) =>
    Ducks.Dms.Selectors.isFeatureDisabled(store, DisabledFeaturesValues.NO_EPG_FILTERS)
  );

  const dispatch = useDispatch();

  const [selectedItem, setSelectedItem] = useState(0);
  const [isShowingChannelsFilters, setIsShowingChannelsFilters] = useState(false);
  const [isShowingCalendar, setIsShowingCalendar] = useState(false);

  useEffect(() => {
    dispatch(ChannelFiltersCreators.getChannelFilters());
  }, [dispatch]);

  useEffect(() => {
    if (filters) {
      const defaultFilter = filters.find((filter) => filter.default);
      if (defaultFilter) {
        setSelectedItem(defaultFilter ? defaultFilter.index : 0);
        setFilter(defaultFilter);
      }
    }
  }, [filters, setFilter]);

  const handleSelectedItem = useCallback(
    (filter: ChannelFilter) => {
      setSelectedItem(filter.index);
      setFilter(filter);
    },
    [setFilter]
  );

  const isDefault = filters[selectedItem]?.id === DEFAULT_FILTER_ID && TimeUtils.isToday(selectedDay);

  return (
    <div className="watchtv-bar component">
      <div className="component-inner">
        <div className="cell">
          {isFiltersDisabled ? null : (
            <div className="filters-wrapper">
              <a
                className="button filters"
                onClick={() => setIsShowingChannelsFilters(true)}
                id="WatchTvBar_button___toggleFilters"
              >
                <span className={`icon filters-icon ${isDefault ? '' : 'red'}`} />
                <h4 id="WatchTvBar_button___filterName">{filters[selectedItem]?.name}</h4>
              </a>
              <Dropdown
                section={section}
                isShowing={isShowingChannelsFilters}
                setIsShowing={setIsShowingChannelsFilters}
                buttons={filters.map((filter) => ({
                  onClick: () => {
                    handleSelectedItem(filter);
                  },
                  id: filter.id,
                  text: filter.name,
                  selected: selectedItem === filter.index,
                }))}
                selectedDay={selectedDay}
                setSelectedDay={setSelectedDay}
              />
            </div>
          )}
          {section === WatchTvSection.TV_GUIDE ? (
            <div className="calendar-wrapper">
              <a
                className="button calendar"
                onClick={() => setIsShowingCalendar(true)}
                id="WatchTvBar_button___toggleCalendar"
              >
                <h4 id="WatchTvBar_button___dayName">{getDayName(selectedDay)}</h4>
              </a>
              <Dropdown
                isShowing={isShowingCalendar}
                setIsShowing={setIsShowingCalendar}
                section={section}
                selectedDay={selectedDay}
                setSelectedDay={setSelectedDay}
              />
            </div>
          ) : null}
        </div>
        <div className="cell">
          <a
            className={`item ${section === WatchTvSection.ON_NOW ? 'selected' : ''}`}
            onClick={() => setSection(WatchTvSection.ON_NOW)}
            id="WatchTvBar_button___onNow"
          >
            <span className="icon on-now-icon" />
            <h4>{translate['watchtv.section.onNow']}</h4>
          </a>
          <a
            className={`item ${section === WatchTvSection.TV_GUIDE ? 'selected' : ''}`}
            onClick={() => setSection(WatchTvSection.TV_GUIDE)}
            id="WatchTvBar_button___tvGuide"
          >
            <span className="icon tv-guide-icon" />
            <h4>{translate['watchtv.section.tvGuide']}</h4>
          </a>
          {overlay && setShowWrapper && <Close bemBlock="WatchTVBar" action={() => setShowWrapper(false)} isActive />}
        </div>
      </div>
    </div>
  );
}

export default React.memo(WatchTvBar);
